import React, { useEffect, useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import DataTable from "../../components/DataTable";
import Modal from "../../components/Modal";
import Alert from "../../components/Alert";
import { listAccountCategories, listLedgers, createLedger, updateLedger, deleteLedger } from "../../api/accountApi";
import "../Users/UsersPage.css";

const emptyForm = { ledgerName: "", categoryId: "", note: "", serial: 0 };

/** Direct port of digicampusbd's Ledger Setup — the chart of accounts every voucher/fee-head-ledger mapping picks from. */
export default function AccountLedgerPage() {
  const [categories, setCategories] = useState([]);
  const [ledgers, setLedgers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [modalMode, setModalMode] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([listAccountCategories(), listLedgers()])
      .then(([cats, ledgerList]) => {
        setCategories(cats);
        setLedgers(ledgerList);
      })
      .catch(() => setError("Failed to load ledgers."))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const openCreate = () => {
    setForm({ ...emptyForm, categoryId: categories.length > 0 ? categories[0].categoryId : "" });
    setModalMode("create");
  };

  const openEdit = (ledger) => {
    setEditingId(ledger.ledgerId);
    setForm({ ledgerName: ledger.ledgerName, categoryId: ledger.categoryId, note: ledger.note || "", serial: ledger.serial || 0 });
    setModalMode("edit");
  };

  const closeModal = () => {
    setModalMode(null);
    setEditingId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const payload = { ledgerName: form.ledgerName, categoryId: Number(form.categoryId), note: form.note, serial: Number(form.serial) };
      if (modalMode === "create") {
        await createLedger(payload);
        setSuccess("Ledger created.");
      } else {
        await updateLedger(editingId, payload);
        setSuccess("Ledger updated.");
      }
      closeModal();
      load();
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to save ledger.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (ledger) => {
    if (!window.confirm(`Delete ledger "${ledger.ledgerName}"?`)) return;
    setError("");
    try {
      await deleteLedger(ledger.ledgerId);
      setSuccess("Ledger deleted.");
      setLedgers((prev) => prev.filter((l) => l.ledgerId !== ledger.ledgerId));
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to delete ledger.");
    }
  };

  const columns = [
    { key: "ledgerName", label: "Ledger" },
    { key: "categoryName", label: "Category" },
    { key: "nature", label: "Nature" },
    { key: "note", label: "Note" },
    { key: "serial", label: "Serial" },
  ];

  return (
    <MainLayout>
      <div className="page-header">
        <h1>Ledger Setup</h1>
        <button type="button" className="btn-primary" onClick={openCreate}>
          + Add Ledger
        </button>
      </div>

      <Alert type="error" onDismiss={() => setError("")}>
        {error}
      </Alert>
      <Alert type="success" onDismiss={() => setSuccess("")}>
        {success}
      </Alert>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <DataTable
          columns={columns}
          rows={ledgers}
          actions={(row) => (
            <>
              <button type="button" className="btn-link" onClick={() => openEdit(row)}>
                Edit
              </button>
              <button type="button" className="btn-link danger" onClick={() => handleDelete(row)}>
                Delete
              </button>
            </>
          )}
        />
      )}

      {modalMode && (
        <Modal title={modalMode === "create" ? "Add Ledger" : "Edit Ledger"} onClose={closeModal}>
          <form onSubmit={handleSubmit} className="user-form">
            <label>Ledger Name</label>
            <input value={form.ledgerName} onChange={(e) => setForm({ ...form, ledgerName: e.target.value })} required />

            <label>Category</label>
            <select value={form.categoryId} onChange={(e) => setForm({ ...form, categoryId: e.target.value })} required>
              {categories.map((c) => (
                <option key={c.categoryId} value={c.categoryId}>
                  {c.categoryName} ({c.nature})
                </option>
              ))}
            </select>

            <label>Note</label>
            <input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />

            <label>Serial</label>
            <input type="number" value={form.serial} onChange={(e) => setForm({ ...form, serial: e.target.value })} />

            <div className="form-actions">
              <button type="button" className="btn-secondary" onClick={closeModal}>
                Cancel
              </button>
              <button type="submit" className="btn-primary" disabled={submitting}>
                {submitting ? "Saving..." : "Save"}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </MainLayout>
  );
}
